// Conversa por voz com o Ordo (como o modo de voz do ChatGPT/Gemini): o
// microfone fica aberto, cada fala vai para o Whisper, a resposta do Claude
// chega em pedaços e cada frase é falada assim que fica pronta. Falar por cima
// interrompe a resposta. A voz vem do servidor (OpenAI) quando ele tem chave;
// senão, usa a voz do sistema (Windows/macOS).

import { api, apiFetch } from "./api.js";

let serverVoice = true; // vira false na primeira vez que o servidor diz que não tem voz (501)

export class VoiceSession {
  // onState(state): "starting" | "listening" | "hearing" | "transcribing" | "thinking" | "speaking"
  // onTurn(question) -> { sources(list), text(text, done), error(msg), remove() }
  constructor({ workspaceId, onState, onLevel, onTurn, onError, alive }) {
    Object.assign(this, { workspaceId, onState, onLevel, onTurn, onError, alive });
    this.history = []; // [{ role: "user" | "assistant", text }]
    this.state = "starting";
    this.muted = false;
    this.bargeIn = true;
    this.turn = null;
    this.turnSeq = 0;
    this.carry = null; // fala interrompida antes da resposta: junta com a próxima
    this.vad = new window.OrdoVad.Vad({ onStart: () => this.speechStarted(), onEnd: (s) => this.utterance(s) });
  }

  async start() {
    this.ctx = new AudioContext({ sampleRate: window.OrdoVad.SAMPLE_RATE });
    await this.ctx.audioWorklet.addModule("pcm-worklet.js");
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    });
    const source = this.ctx.createMediaStreamSource(this.stream);
    const node = new AudioWorkletNode(this.ctx, "pcm-capture");
    const sink = this.ctx.createGain();
    sink.gain.value = 0;
    node.port.onmessage = (e) => this.block(e.data);
    source.connect(node);
    node.connect(sink);
    sink.connect(this.ctx.destination);
    this.setState("listening");
  }

  stop() {
    if (this.stopped) return;
    this.stopped = true;
    this.cancelTurn();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.ctx?.close().catch(() => {});
  }

  setState(state) {
    this.state = state;
    // Com o Ordo falando, só uma voz mais alta (a sua, perto do microfone) interrompe.
    this.vad.sensitivity = state === "speaking" ? 2.5 : 1;
    this.onState?.(state);
  }

  setMuted(muted) {
    this.muted = muted;
    if (muted) this.vad.reset();
  }

  block(samples) {
    if (this.stopped) return;
    if (this.alive && !this.alive()) return this.stop(); // a tela foi trocada
    // A voz do sistema não passa pelo cancelamento de eco: o microfone ouviria o próprio Ordo.
    const deaf = this.muted || (this.state === "speaking" && (!this.bargeIn || !serverVoice));
    if (deaf) {
      this.vad.reset();
      return this.onLevel?.(0);
    }
    this.onLevel?.(this.vad.push(samples));
  }

  speechStarted() {
    const t = this.turn;
    if (t && !t.spoken && !t.recorded) {
      // Recomeçou a falar antes da resposta sair: a fala anterior era só uma pausa.
      this.carry = t.full.catch(() => "");
      t.view?.remove();
      this.cancelTurn();
    } else if (t) {
      this.interrupt();
    }
    this.setState("hearing");
  }

  // Para a resposta atual (botão ou falando por cima).
  interrupt() {
    const t = this.turn;
    if (t?.question && !t.recorded && t.text) {
      this.remember(t, t.text + " …(interrompido)");
    }
    this.cancelTurn();
    if (!this.stopped) this.setState("listening");
  }

  // Envia agora o que já foi falado, sem esperar a pausa.
  sendNow() {
    this.vad.finish();
  }

  cancelTurn() {
    const t = this.turn;
    this.turn = null;
    if (!t) return;
    t.ctrl.abort();
    t.speaker?.stop();
  }

  remember(turn, answer) {
    turn.recorded = true;
    this.history.push({ role: "user", text: turn.question }, { role: "assistant", text: answer });
    this.history = this.history.slice(-12);
  }

  async utterance(samples) {
    const turn = { id: ++this.turnSeq, ctrl: new AbortController(), spoken: false, text: "" };
    this.turn = turn;
    this.setState("transcribing");
    const wav = window.OrdoChunker.encodeWav(samples);
    const stt = api("POST", "/ai/voice/stt", wav, { raw: true, contentType: "audio/wav", timeout: 60000 }).then((r) => r.text || "");
    turn.full = Promise.all([this.carry || "", stt]).then((parts) => parts.filter(Boolean).join(" ").trim());
    this.carry = null;
    let question;
    try {
      question = await turn.full;
    } catch (err) {
      if (this.turn !== turn) return;
      this.turn = null;
      this.onError?.(err.message);
      return this.setState("listening");
    }
    if (this.turn !== turn) return;
    if (!question) {
      this.turn = null;
      return this.setState("listening");
    }
    turn.question = question;
    turn.view = this.onTurn(question);
    this.setState("thinking");
    await this.answer(turn);
  }

  async answer(turn) {
    const speaker = new Speaker(turn.ctrl.signal, () => {
      turn.spoken = true;
      if (this.turn === turn) this.setState("speaking");
    });
    turn.speaker = speaker;
    try {
      const res = await apiFetch("POST", "/ai/voice/ask",
        { question: turn.question, history: this.history, workspaceId: this.workspaceId || undefined }, { signal: turn.ctrl.signal });
      for await (const ev of ndjson(res.body)) {
        if (this.turn !== turn) return;
        if (ev.type === "sources") turn.view.sources(ev.sources || []);
        else if (ev.type === "delta") {
          turn.text += ev.text;
          turn.view.text(turn.text, false);
          speaker.feed(ev.text);
        } else if (ev.type === "error") throw new Error(ev.error);
      }
      if (this.turn !== turn) return;
      speaker.end();
      turn.view.text(turn.text, true);
      this.remember(turn, turn.text);
      await speaker.done;
      if (this.turn === turn) {
        this.turn = null;
        this.setState("listening");
      }
    } catch (err) {
      if (err.name === "AbortError" || this.turn !== turn) return;
      speaker.stop();
      turn.view.error(err.message);
      this.turn = null;
      this.setState("listening");
    }
  }
}

// Fala as frases na ordem em que chegam. Com a voz do servidor, pede o áudio
// de cada frase assim que ela fica pronta, enquanto a anterior ainda toca.
class Speaker {
  constructor(signal, onStart) {
    this.signal = signal;
    this.onStart = onStart;
    this.buf = "";
    this.queue = [];
    this.count = 0;
    this.done = new Promise((r) => { this.resolve = r; });
  }

  feed(text) {
    this.buf += text;
    for (;;) {
      // A primeira frase sai o quanto antes; as outras em pedaços maiores, com entonação melhor.
      const min = this.count === 0 ? 12 : 60;
      const re = /[.!?…:;](?=\s)|\n/g;
      let cut = -1;
      let m;
      while ((m = re.exec(this.buf))) {
        if (m.index + 1 >= min) { cut = m.index + 1; break; }
      }
      if (cut < 0) break;
      this.say(this.buf.slice(0, cut));
      this.buf = this.buf.slice(cut);
    }
  }

  end() {
    this.ended = true;
    this.say(this.buf);
    this.buf = "";
    this.pump();
  }

  say(raw) {
    const text = spoken(raw);
    if (!text) return;
    this.count++;
    const audio = serverVoice ? fetchSpeech(text, this.signal) : null;
    audio?.catch(() => {}); // tratado em play()
    this.queue.push({ text, audio });
    this.pump();
  }

  async pump() {
    if (this.playing || this.stopped) return;
    this.playing = true;
    while (this.queue.length && !this.stopped) await this.play(this.queue.shift());
    this.playing = false;
    if (this.stopped || (this.ended && !this.queue.length)) this.resolve();
  }

  async play(item) {
    let blob = null;
    if (item.audio) {
      try {
        blob = await item.audio;
      } catch (err) {
        if (err.name === "AbortError") return;
        if (err.status === 501) serverVoice = false;
        else console.warn("voz do servidor falhou; usando a do sistema", err);
      }
    }
    if (this.stopped) return;
    this.onStart();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      this.current = a;
      await new Promise((r) => {
        a.onended = r;
        a.onerror = r;
        a.onpause = r;
        a.play().catch(r);
      });
      URL.revokeObjectURL(url);
    } else {
      await systemSpeak(item.text);
    }
  }

  stop() {
    this.stopped = true;
    this.queue = [];
    this.current?.pause();
    if (window.speechSynthesis?.speaking) window.speechSynthesis.cancel();
    this.resolve();
  }
}

async function fetchSpeech(text, signal) {
  const res = await apiFetch("POST", "/ai/voice/tts", { text }, { signal });
  return res.blob();
}

function systemSpeak(text) {
  const synth = window.speechSynthesis;
  if (!synth) return Promise.resolve();
  return new Promise((resolve) => {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "pt-BR";
    u.rate = 1.05;
    const voice = systemVoice();
    if (voice) u.voice = voice;
    // Se a voz do sistema travar, segue a conversa depois de um tempo proporcional ao texto.
    const timer = setTimeout(resolve, 4000 + text.length * 120);
    u.onend = u.onerror = () => { clearTimeout(timer); resolve(); };
    synth.speak(u);
  });
}

// Prefere as vozes neurais em português do Brasil, quando o sistema tem.
function systemVoice() {
  const voices = (window.speechSynthesis?.getVoices() || []).filter((v) => /^pt[-_]BR/i.test(v.lang));
  return voices.find((v) => /natural|online|neural/i.test(v.name))
    || voices.find((v) => /francisca|thalita|antonio|luciana|maria/i.test(v.name))
    || voices[0] || null;
}

// Tira o que não se fala: Markdown, links e IDs.
function spoken(text) {
  return String(text)
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_#`>|~]/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

async function* ndjson(body) {
  const reader = body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let i;
    while ((i = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, i).trim();
      buf = buf.slice(i + 1);
      if (line) yield JSON.parse(line);
    }
  }
  if (buf.trim()) yield JSON.parse(buf);
}
